import { useCallback, useState, useMemo } from 'react';

import {
  PlatformTableColumnValueListItem,
  PlatformTableFilterModeParameter,
  usePlatformTableColumnListValuesTeam,
} from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';

import { useTableStateContext } from './useTableStateContext';

// Define the type for value options, mirroring FieldOption for consistency
interface ValueOption {
  name: string;
}

/**
 * Hook that fetches possible values for a given field based on internal state.
 * Provides a function to update the fieldName and inputText criteria.
 * @returns An object containing the suggestion options, loading/error states, and the update function.
 */
export const useGetValueOptions = () => {
  const { currentMembership } = useBaseAuthenticated();
  const { tableName } = useTableStateContext();
  const [currentFieldName, setCurrentFieldName] = useState<string>('');
  const [currentInputText, setCurrentInputText] = useState<string | undefined>(undefined);

  // Prepare params for the query hook
  const queryParams = useMemo(
    () => ({
      ...(currentInputText?.trim().length
        ? {
            filter: `${currentInputText.trim()}*`,
          }
        : {}),
      filter_mode: PlatformTableFilterModeParameter.column,
      per_page: 1000, // Suggestion limit
    }),
    [currentInputText],
  );

  // Call the hook at the top level, driven by state
  const { data, isError, isFetching, isLoading } = usePlatformTableColumnListValuesTeam(
    currentMembership.team.name,
    tableName,
    currentFieldName,
    queryParams,
    {
      query: {
        enabled: !!tableName && !!currentFieldName,
        keepPreviousData: true, // Optionally keep stale data while loading new field/text
      },
    },
  );

  // Function to trigger an update by setting the state
  const updateCriteria = useCallback((fieldName: string, inputText?: string) => {
    setCurrentFieldName(fieldName);
    setCurrentInputText(inputText);
  }, []);

  // Memoize the transformation of the raw data into ValueOption[]
  const options = useMemo((): ValueOption[] => {
    if (isLoading || isError || !data?.data?.items) {
      return []; // Return empty array while loading, on error, or if no data
    }

    // Process the fetched column value data
    return data.data.items
      .map(({ value }: PlatformTableColumnValueListItem) => {
        return {
          name: String(value ?? ''),
        };
      })
      .filter((option) => option.name !== ''); // Filter out empty strings if necessary
  }, [data, isLoading, isError]);

  return {
    isError,
    isLoading: isLoading || isFetching,

    updateValueSuggestions: updateCriteria,
    // Rename the function to reflect its action
    valueSuggestions: options,
  };
};
